import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react'

import { pick } from 'ramda'

import { useAllowedAnnotationTypes } from '~/src/Annotations/utils'
import {
  DESKTOP_ITEM_HEIGHT,
  MOBILE_ITEM_HEIGHT,
  SCOPING_PROP_DEFAULT_VALUE,
  SCOPING_PROPS,
} from '~/src/Journal/constants'
import { EPOCH_DATE } from '~/src/Lib/Constants'
import createLogger from '~/src/Lib/Logging'
import {
  defer,
  EMPTY_OBJECT,
  getDateTime,
  useBreakpoint,
} from '~/src/Lib/Utils'

import { getFetch } from './callbacks'
import {
  CUSTOM_ACTIONS,
  getDispatcher,
  getStateInitializer,
  reducer,
} from './reducer'
import {
  getStepDays,
  getTopItemIndex,
  isFiltered,
  prepareScope,
} from './selectors'

export * from './callbacks'
export { default as JournalContext } from './context'
export * from './reducer'
export * from './selectors'

const logger = createLogger('Journal/utils')
const propsPicker = pick(['breakpoint'])
export const autoFetcher = (state, fetch, dispatch) => {
  const {
    autoFetch,
    deviceCount = 100,
    loaded,
    loading,
    selectedDate
  } = state
  if (!autoFetch || loading) return
  const filters = prepareScope(state)
  const days = getStepDays(isFiltered(filters, state), deviceCount)
  const start = selectedDate.minus({ days })
  const end = selectedDate.plus({ days })
  dispatch('autoFetch', false)

  fetch({
    ...filters,
    start,
    end,
  }, !loaded)
    .finally(() => {
      defer(() => dispatch('loaded', true), 500)
    })
}
export const fetchWhenFewItems = async ({
  dispatch,
  fetch,
  state = EMPTY_OBJECT
}) => {
  const { journal, loaded, loading: stateLoading } = state
  const { before, after, items, loading: journalLoading, params = EMPTY_OBJECT } = journal
  const loading = Boolean(stateLoading || journalLoading)
  const { start: oldStart, end: oldEnd } = params
  const itemCount = items?.length ?? 0
  const totalItemCount = (before ?? 0) + (after ?? 0) + itemCount
  const fewItems = itemCount < 20
  const hasMoreItems = totalItemCount && itemCount < totalItemCount

  if (loaded && !loading && fewItems && hasMoreItems) {
    let start = getDateTime(oldStart)
    let end = getDateTime(oldEnd)
    if (before) {
      start = before < 100
        ? getDateTime(EPOCH_DATE)
        : start.minus({ months: 1 }).startOf('week')
    }
    if (after) {
      end = after < 200
        ? getDateTime(oldEnd).plus({ years: 1 }).endOf('month')
        : end.plus({ months: 1 }).endOf('week')
    }

    if (+start === +getDateTime(oldEnd) && +end === +getDateTime(oldEnd)) return null
    dispatch('loaded', false)

    const payload = {
      ...prepareScope(state),
      start: start ?? getDateTime(state.journal.start),
      end: end ?? getDateTime(state.journal.end),
    }
    const innerEffect = async fetchParams => {
      await fetch(fetchParams)

      dispatch('loaded', true)
    }

    return innerEffect(payload)
  }
  return null
}

export const updateScoping = (props, state, dispatch) => {
  const changedScope = SCOPING_PROPS.filter(key => (
    props[key] !== SCOPING_PROP_DEFAULT_VALUE && props[key]?.id !== state[key]?.id
  ))
  if (!changedScope.length) {
    return
  }
  const payload = pick(changedScope, props)
  const idsChanged = changedScope.some(key => props[key]?.id !== state[key]?.id)
  dispatch({
    ...payload,
    // If any scoping ID has changed, we need to replace the journal items with only those for the new scope
    autoFetch: idsChanged,
    loaded: !idsChanged,
    autoScroll: idsChanged,
  })
}

export default (props, height) => {
  const systemAllowedTypes = useAllowedAnnotationTypes()
  const [state, dispatchRaw] = useReducer(reducer, props, getStateInitializer(systemAllowedTypes))
  const listRef = useRef(null)
  const breakpoint = useBreakpoint()
  const { journal } = state
  const { before = 0, after = 0, items } = journal
  const itemCount = (items?.length ?? 0)
  const totalItemCount = before + after + itemCount
  const itemHeight = breakpoint === 'xs' ? MOBILE_ITEM_HEIGHT : DESKTOP_ITEM_HEIGHT
  const dispatch = getDispatcher(dispatchRaw)

  const fetch = getFetch(props, dispatch)

  useEffect(() => {
    dispatch('journal', props.journal)
  }, [dispatch, props.journal])

  useEffect(() => {
    if (!Array.isArray(props.activeTypes)) return
    dispatch('activeTypes', props.activeTypes)
  }, [dispatch, props.activeTypes])

  useEffect(
    () => autoFetcher(state, fetch, dispatch),
    [state, fetch, dispatch]
  )

  useEffect(
    () => {
      const visibleMax = Math.ceil(height / itemHeight)
      if (
        !state.loaded
        || (itemCount && itemCount > (visibleMax * 2))
        || !before
      ) {
        logger.debug('fetchWhenFewItems skipping', {
          loaded: state.loaded,
          itemCount,
          visibleMax,
          before
        })
        return
      }
      logger.debug('fetchWhenFewItems called', { itemCount, visibleMax, before })
      // keep with braces because fetchWhenFewItems returns a promise
      fetchWhenFewItems({ dispatch, fetch, state })
    },
    // ~eslint-disable-next-line react-hooks/exhaustive-deps
    [before, breakpoint, dispatch, fetch, height, itemCount, itemHeight, state]
  )
  useEffect(
    () => updateScoping(props, state, dispatch),
    // We'll end up in an infinite loop if we call this every time state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, props]
  )
  // our infinite loading reactor
  useEffect(() => {
    const { infiniteLoading, previousInfiniteLoading, loading, loadMore } = state
    if (loading || previousInfiniteLoading === infiniteLoading) {
      return
    }

    if (infiniteLoading && loadMore) {
      const {
        journal: {
          items: journalItems,
          lastFetched
        }
      } = state
      const startDate = loadMore.start.toISODate()
      const endDate = loadMore.end.toISODate()
      const haveStart = Boolean(journalItems.find(item => item.localTimestamp?.startsWith(startDate)))
      const haveEnd = Boolean(journalItems.find(item => item.localTimestamp?.startsWith(endDate)))

      if (haveStart && haveEnd && lastFetched > Date.now() - 5000) {
        loadMore.resolve(true)
        dispatch({ infiniteLoading: false, loadMore: null })
        return
      }
      // Need to load more items
      const infiniteLoaderFetch = async ({ resolve, reject, start, end }, params) => {
        logger.debug('infiniteLoading fetch called', { start: start.toISODate(), end: end.toISODate(), params })
        try {
          await fetch({ ...params, start, end })
          resolve(true)
        } catch (error) {
          console.error('infiniteLoading fetch failed:', { error, ...loadMore })
          reject(false)
        }

        defer(() => dispatch('infiniteLoading', false), defer.priorities.low)
      }
      infiniteLoaderFetch(loadMore, prepareScope(state))
      dispatch('loadMore', null)
    }
  }, [dispatch, fetch, state])

  const loadMoreItems = useCallback((start, end) => new Promise((resolve, reject) => {
    logger.debug('loadMoreItems called', { start, end })
    dispatch({
      infiniteLoading: true,
      loadMore: { start, end, resolve, reject },
    })
  }), [dispatch])

  const isItemLoaded = useCallback(
    index => index >= journal?.after && index < ((journal?.after ?? 0) + (journal?.items?.length ?? 0)),
    [journal?.after, journal?.items?.length]
  )

  const goToNextMonth = useCallback(() => dispatch({
    type: CUSTOM_ACTIONS.goToNextMonth
  }), [dispatch])
  const goToPrevMonth = useCallback(() => dispatch({
    type: CUSTOM_ACTIONS.goToPrevMonth
  }), [dispatch])

  const debounceAutoScroll = useRef(0)

  useLayoutEffect(() => {
    debounceAutoScroll.current = Date.now()
  }, [state.scrollerIndices])

  useLayoutEffect((() => {
    const { autoScroll, loaded, loading, scrollerIndices: { visibleStartIndex } } = state
    if (!autoScroll || !loaded || loading) return
    if (Date.now() - debounceAutoScroll.current < 66) {
      debounceAutoScroll.current = Date.now()
      return
    }
    const topIndex = getTopItemIndex(state)

    if (listRef.current && topIndex > -1 && topIndex !== visibleStartIndex) {
      dispatch('autoScroll', false)
      listRef.current.scrollToItem?.(topIndex, 'start')
      debounceAutoScroll.current = Date.now()
    }
  }), [dispatch, state])

  return {
    ...state,
    ...propsPicker(props),
    dispatch,
    fetch,
    itemCount,
    totalItemCount,
    listRef,
    loadMoreItems,
    isItemLoaded,
    goToNextMonth,
    goToPrevMonth,
  }
}
